/*
  Slendr cart drawer behaviour:
  - Upsell "Add" buttons (data-cart-upsell-add)
  - Shipping protection toggle (<shipping-protection-toggle>)
  - "On by default" auto-add, fired only when the drawer OPENS (never on a passive pageload)
  Refreshes the drawer with the same Section Rendering pattern the theme's cart.js uses.
*/
(function () {
  'use strict';

  const OPTOUT_KEY = 'slendr:sp-optout'; // localStorage: a customer's removal sticks across sessions
  let protectionBusy = false; // in-flight lock so protection can't be added/removed twice at once

  function routeUrl(name, fallback) {
    return (window.routes && window.routes[name]) || fallback;
  }
  const SECTIONS = ['cart-drawer', 'cart-icon-bubble'];

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((r) => r.json());
  }

  function innerHTMLOf(htmlString, selector) {
    const node = new DOMParser().parseFromString(htmlString, 'text/html').querySelector(selector);
    return node ? node.innerHTML : null;
  }

  function optedOut() {
    try {
      return localStorage.getItem(OPTOUT_KEY) === '1';
    } catch (e) {
      return false;
    }
  }
  function setOptOut(v) {
    try {
      if (v) localStorage.setItem(OPTOUT_KEY, '1');
      else localStorage.removeItem(OPTOUT_KEY);
    } catch (e) {
      /* private mode */
    }
  }

  // `publish`/`subscribe`/`PUB_SUB_EVENTS` are top-level globals from the theme's pubsub.js
  // (PUB_SUB_EVENTS is a `const`, so it is NOT on window — reference it bare, guarded by typeof).
  function publishUpdate(cartData) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      try {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'slendr-cart', cartData: cartData || null });
      } catch (e) {
        /* no-op */
      }
    }
  }

  // Re-render the drawer + cart bubble from a cart API response that included `sections`.
  function applySections(state) {
    const sections = state && state.sections;
    if (!sections) return state;

    if (sections['cart-drawer']) {
      const html = innerHTMLOf(sections['cart-drawer'], '#CartDrawer');
      const target = document.querySelector('#CartDrawer');
      if (html != null && target) target.innerHTML = html;
    }

    if (sections['cart-icon-bubble']) {
      const bubbleEl = document.getElementById('cart-icon-bubble');
      const src = innerHTMLOf(sections['cart-icon-bubble'], '.shopify-section');
      if (src != null && bubbleEl) bubbleEl.innerHTML = src;
    }

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      // Derive emptiness from what actually rendered (the add.js response has no item_count).
      cartDrawer.classList.toggle('is-empty', !document.querySelector('#CartDrawer .cart-item'));
    }

    // The overlay node was replaced; re-bind its close handler once.
    const overlay = document.querySelector('#CartDrawer-Overlay');
    if (overlay && !overlay.dataset.slendrBound && cartDrawer && typeof cartDrawer.close === 'function') {
      overlay.dataset.slendrBound = '1';
      overlay.addEventListener('click', function () {
        cartDrawer.close();
      });
    }

    return state;
  }

  // Add 1 of a variant via /cart/add.js. Used for upsell adds AND for adding shipping
  // protection (toggle-on / auto-add): add.js can create a line that isn't in the cart yet,
  // whereas change.js can only modify a line that already exists.
  function addVariant(variantId) {
    return postJSON(routeUrl('cart_add_url', '/cart/add.js'), {
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: SECTIONS,
      sections_url: window.location.pathname,
    }).then((state) => {
      applySections(state);
      publishUpdate(); // best-effort; add.js does not return full cart, so no cartData
      return state;
    });
  }

  // Set the quantity of a variant that is ALREADY in the cart, via /cart/change.js.
  // Used to remove shipping protection (quantity 0).
  function setVariantQuantity(variantId, quantity) {
    return postJSON(routeUrl('cart_change_url', '/cart/change.js'), {
      id: String(variantId),
      quantity: quantity,
      sections: SECTIONS,
      sections_url: window.location.pathname,
    }).then((state) => {
      applySections(state);
      publishUpdate(state); // change.js returns the full cart
      return state;
    });
  }

  /* ---------- Upsell add buttons ---------- */
  document.addEventListener('click', function (event) {
    const btn = event.target.closest('[data-cart-upsell-add]');
    if (!btn || btn.disabled) return;
    event.preventDefault();
    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    btn.setAttribute('aria-busy', 'true');
    addVariant(variantId)
      .catch(function (e) {
        console.error('[slendr cart] upsell add failed', e);
      })
      .finally(function () {
        // The card may have been re-rendered away; clear on whatever live button still matches.
        const live = document.querySelector('[data-cart-upsell-add][data-variant-id="' + variantId + '"]');
        if (live) live.removeAttribute('aria-busy');
      });
  });

  /* ---------- Shipping protection toggle ---------- */
  class ShippingProtectionToggle extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-shipping-protection-input]');
      this.variantId = this.dataset.variantId;
      if (!this.variantId || !this.input) return;
      if (!this.input.dataset.bound) {
        this.input.dataset.bound = 'true';
        this.input.addEventListener('change', this.onToggle.bind(this));
      }
    }

    setBusy(state) {
      const sw = this.querySelector('.slendr-switch');
      if (sw) sw.setAttribute('aria-busy', state ? 'true' : 'false');
    }

    onToggle() {
      const checked = this.input.checked;
      this.setBusy(true);
      if (checked) {
        setOptOut(false);
        addVariant(this.variantId).catch((e) => {
          console.error('[slendr cart] protection add failed', e);
          this.input.checked = false;
          this.setBusy(false);
        });
      } else {
        setOptOut(true);
        setVariantQuantity(this.variantId, 0).catch((e) => {
          console.error('[slendr cart] protection remove failed', e);
          this.input.checked = true;
          this.setBusy(false);
        });
      }
    }
  }

  if (!customElements.get('shipping-protection-toggle')) {
    customElements.define('shipping-protection-toggle', ShippingProtectionToggle);
  }

  // Add protection when the drawer opens IF there's a real product and protection isn't
  // already in the cart (and the customer hasn't switched it off). Reading "in cart" from the
  // freshly-rendered toggle prevents double-adds; there is NO once-per-page guard, so removing
  // and re-adding a product re-adds protection too.
  function maybeAutoAddProtection() {
    const toggle = document.querySelector('shipping-protection-toggle[data-cart-protection]');
    if (!toggle || protectionBusy) return;
    const variantId = toggle.dataset.variantId;
    if (!variantId) return;
    const defaultOn = toggle.dataset.defaultOn === 'true';
    const inCart = toggle.dataset.inCart === 'true';
    const hasRealItem = !!document.querySelector('#CartDrawer .cart-item');
    if (defaultOn && !inCart && hasRealItem && !optedOut()) {
      protectionBusy = true;
      if (typeof toggle.setBusy === 'function') toggle.setBusy(true);
      addVariant(variantId)
        .catch((e) => console.error('[slendr cart] protection auto-add failed', e))
        .finally(() => {
          protectionBusy = false;
        });
    }
  }

  // Patch the drawer's open() so auto-add runs each time it opens.
  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('cart-drawer').then(function () {
      const proto = customElements.get('cart-drawer').prototype;
      if (proto && !proto.__slendrOpenPatched) {
        proto.__slendrOpenPatched = true;
        const originalOpen = proto.open;
        proto.open = function () {
          const result = originalOpen.apply(this, arguments);
          setTimeout(maybeAutoAddProtection, 0);
          return result;
        };
      }
    });
  }

  // If protection is the only line left (e.g. the last real product was removed), remove it so the cart is truly empty.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (!event || event.source === 'slendr-cart') return;
      const data = event.cartData;
      if (!data || !Array.isArray(data.items)) return;
      const toggle = document.querySelector('shipping-protection-toggle[data-cart-protection]');
      const variantId = toggle ? Number(toggle.dataset.variantId) : null;
      if (!variantId) return;
      if (data.items.length === 1 && Number(data.items[0].variant_id) === variantId) {
        setVariantQuantity(variantId, 0).catch(function () {});
      }
    });
  }
})();
